import { localDateKey } from './wallClock';
import sprites from '../data/armorSprites.json';

// Progression i bundbaren. Alt regnes klient-side af eventsene, så der er ingen lagret
// tilstand. XP henfalder: hver hverdag mister de optjente XP lidt værdi (halveringstid
// HALF_LIFE_DAYS hverdage), så levelet følger det nylige tempo og falder, hvis man står stille.
// Weekender og dage med People-timer (ferie, sygdom) tæller ikke med i henfaldet. Der er
// ingen nulstilling 1. januar: kalenderen henter logs fra juli sidste år, og det er opvarmningen.
export const XP_PER_HOUR = 125;
export const XP_DAILY_QUEST = 500;
export const XP_WEEKLY_QUEST = 2000;
// Levelkurven: level 1 er 0 XP, level 2 er 125 XP (1 time), og hvert level koster mere end det forrige.
// Level n begynder ved XP_FIRST_LEVEL * (n - 1) ^ CURVE_POWER. Eksponenten er valgt, så TOP_LEVEL
// begynder ved TOP_XP, og det er ca. ligevægten for 10 timer om dagen (50 om ugen) med henfaldet.
export const XP_FIRST_LEVEL = 125;
export const TOP_LEVEL = 50;
export const TOP_XP = 375000;
const CURVE_POWER = Math.log(TOP_XP / XP_FIRST_LEVEL) / Math.log(TOP_LEVEL - 1);
export const HALF_LIFE_DAYS = 120;
// Et trin tabes først, når levelet er faldet mere end STAGE_BUFFER under trinnets start.
export const STAGE_BUFFER = 2;
const DAILY_DECAY = 0.5 ** (1 / HALF_LIFE_DAYS);
// Level hvor trin 2-10 begynder.
export const STAGE_AT = [5, 11, 16, 22, 27, 33, 38, 44, 50];
export const DEFAULT_WEEK_NORM = 37.5;

const STAGE_NAMES = sprites.stages.map((s) => s.name);
const MAX_STREAK_LOOKBACK_DAYS = 400;

const hoursOf = (ev) => (new Date(ev.end) - new Date(ev.start)) / 3600000;
const dayOf = (year, month, day) => new Date(year, month, day);
const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;

// Mandagen i ugen (ISO) som YYYY-MM-DD.
export const isoWeekKey = (date) => {
  const d = new Date(date);
  return localDateKey(dayOf(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 6) % 7)));
};

// XP hvor level n begynder, og det level en given XP-sum giver (modsat funktion, rettet for afrunding).
export const xpForLevel = (n) => (n <= 1 ? 0 : Math.round(XP_FIRST_LEVEL * (n - 1) ** CURVE_POWER));
export const levelOfXp = (xp) => {
  let n = 1 + Math.floor((Math.max(0, xp) / XP_FIRST_LEVEL) ** (1 / CURVE_POWER));
  while (xpForLevel(n) > xp) n -= 1;
  while (xpForLevel(n + 1) <= xp) n += 1;
  return Math.max(1, n);
};

export const stageOfLevel = (level) => 1 + STAGE_AT.filter((l) => l <= level).length;

// Trinnet stiger straks, men falder først, når levelet er STAGE_BUFFER under trinnets start.
export const stageAfter = (stage, level) => {
  const up = stageOfLevel(level);
  if (up > stage) return up;
  const down = stageOfLevel(level + STAGE_BUFFER);
  return down < stage ? down : stage;
};

export function computeProgress({
  events,
  weekNorm = DEFAULT_WEEK_NORM,
  now = new Date(),
  isOutlookSyncEvent = () => false,
  isBillableEvent = () => false,
}) {
  const norm = weekNorm > 0 ? weekNorm : DEFAULT_WEEK_NORM;
  const dayNorm = norm / 5;
  const year = now.getFullYear();
  const todayKey = localDateKey(now);
  const monthPrefix = todayKey.slice(0, 7);

  // Pr. dag: timer i alt og People-timer (People tæller kun til normerne), og dage med en log (streak).
  const days = new Map();
  const loggedDays = new Set();
  let projectsHours = 0;
  let billable = 0;
  let nonBillable = 0;

  (events || []).forEach((ev) => {
    if (!ev || ev.logId?.startsWith?.('temp-') || isOutlookSyncEvent(ev)) return;
    const hours = hoursOf(ev);
    if (!(hours > 0)) return;
    const key = localDateKey(ev.start);
    loggedDays.add(key);
    const day = days.get(key) || { total: 0, people: 0 };
    day.total += hours;
    if (ev.isPeople) day.people += hours;
    days.set(key, day);
    if (!ev.isPeople && new Date(ev.start).getFullYear() === year) {
      projectsHours += hours;
      if (isBillableEvent(ev)) billable += hours;
      else nonBillable += hours;
    }
  });

  let perfectDaysThisMonth = 0;
  let weekHours = 0;
  const thisWeek = isoWeekKey(now);
  days.forEach((day, key) => {
    if (day.total >= dayNorm && key.startsWith(monthPrefix)) perfectDaysThisMonth += 1;
    const [y, m, d] = key.split('-').map(Number);
    if (isoWeekKey(dayOf(y, m - 1, d)) === thisWeek) weekHours += day.total;
  });

  // XP dag for dag fra den første log. Timer, dagsquest (dagen når dagsnormen) og ugequest (ugen
  // når ugenormen) lægges til. Henfaldet rammer hverdage, der er gået, og i dag, når der er en log
  // (så levelet ikke dykker, før dagens første log er ført). Fremtidige logs henfalder ikke.
  let xpRaw = 0;
  let stage = 1;
  const keys = [...days.keys()].sort();
  if (keys.length) {
    const lastKey = keys[keys.length - 1] > todayKey ? keys[keys.length - 1] : todayKey;
    const [fy, fm, fd] = keys[0].split('-').map(Number);
    let weekKey = null;
    let weekSoFar = 0;
    for (let c = dayOf(fy, fm - 1, fd); localDateKey(c) <= lastKey; c.setDate(c.getDate() + 1)) {
      const key = localDateKey(c);
      const day = days.get(key);
      const hours = day?.total || 0;
      const wk = isoWeekKey(c);
      if (wk !== weekKey) { weekKey = wk; weekSoFar = 0; }
      if (!isWeekend(c) && (key < todayKey || (key === todayKey && hours > 0))) {
        xpRaw *= DAILY_DECAY ** (1 - Math.min(1, (day?.people || 0) / dayNorm));
      }
      if (day) {
        // Kun Projects-timer giver XP: timer og quests regnes uden People.
        const projHours = day.total - day.people;
        xpRaw += projHours * XP_PER_HOUR + (projHours >= dayNorm ? XP_DAILY_QUEST : 0);
        if (weekSoFar < norm && weekSoFar + projHours >= norm) xpRaw += XP_WEEKLY_QUEST;
        weekSoFar += projHours;
      }
      stage = stageAfter(stage, levelOfXp(Math.round(xpRaw)));
    }
  }

  const xp = Math.round(xpRaw);
  const level = levelOfXp(xp);
  const nextStageLevel = STAGE_AT[stage - 1] ?? null;

  const dayHours = days.get(todayKey)?.total || 0;

  // Streak: hverdage i træk med mindst én log, talt baglæns fra i dag (eller i går, hvis i dag er tom).
  let streak = 0;
  let cursor = dayOf(now.getFullYear(), now.getMonth(), now.getDate());
  if (!loggedDays.has(localDateKey(cursor))) cursor.setDate(cursor.getDate() - 1);
  for (let i = 0; i < MAX_STREAK_LOOKBACK_DAYS; i += 1) {
    if (!isWeekend(cursor)) {
      if (!loggedDays.has(localDateKey(cursor))) break;
      streak += 1;
    }
    cursor.setDate(cursor.getDate() - 1);
  }

  const billableTotal = billable + nonBillable;

  return {
    ytdHours: projectsHours,
    xp,
    level,
    stage,
    stageName: STAGE_NAMES[stage - 1],
    xpIntoLevel: xp - xpForLevel(level),
    xpForNextLevel: xpForLevel(level + 1) - xpForLevel(level),
    nextStageName: nextStageLevel === null ? null : STAGE_NAMES[stage],
    nextStageLevel,
    hoursToNextStage: nextStageLevel === null ? null : Math.max(0, (xpForLevel(nextStageLevel) - xp) / XP_PER_HOUR),
    dayHours,
    dayNorm,
    dayDone: dayHours >= dayNorm,
    weekHours,
    weekNorm: norm,
    weekDone: weekHours >= norm,
    streak,
    perfectDaysThisMonth,
    billableShare: billableTotal > 0 ? Math.round((billable / billableTotal) * 100) : 0,
  };
}

// Rustningsstigen til oversigten: ét trin pr. række med status og timer til trinnet begynder.
export function armorLadder({ xp, stage }) {
  return sprites.stages.map((s, i) => {
    const n = i + 1;
    const startLevel = n === 1 ? 1 : STAGE_AT[i - 1];
    const endLevel = STAGE_AT[i] === undefined ? null : STAGE_AT[i] - 1;
    return {
      stage: n,
      name: s.name,
      startLevel,
      endLevel,
      status: n < stage ? 'passed' : n === stage ? 'current' : 'upcoming',
      hoursToGo: n > stage ? Math.max(0, (xpForLevel(startLevel) - xp) / XP_PER_HOUR) : 0,
    };
  });
}

// Procent af hele stigen (TOP_LEVEL = 100 %, kan overstige 100). Det er det eneste tal ud over level, leaderboardet viser.
export const yearPct = (xp) => Math.round((xp / TOP_XP) * 1000) / 10;
